import { useMemo } from 'react';

import { CARD_REGISTRY } from '../config/cardRegistry';
import { DASHBOARD_RULES } from '../config/cardRules';
import type { CardConfig, DashboardConfig } from '../config/types';

import { useDashboardState } from './useDashboardState';

/**
 * Main hook for dashboard configuration.
 * This is the single source of truth for determining which cards to display
 * and in what order, based on the current application state.
 * 
 * @returns {DashboardConfig} Complete dashboard configuration including cards array
 */
export const useDashboardConfig = (): DashboardConfig => {
  // Get aggregated dashboard state
  const state = useDashboardState();

  // Generate card configuration based on business rules
  const cards = useMemo(() => {
    // Don't process rules while loading
    if (state.isLoading) {
      return [];
    }

    // Filter rules based on conditions, then sort by priority
    const visibleCards = DASHBOARD_RULES
      .filter(rule => {
        try {
          return rule.conditions(state);
        } catch (error) {
          console.error(`Error evaluating rule for card ${rule.id}:`, error);
          return false;
        }
      })
      .sort((a, b) => a.priority - b.priority)
      .map(rule => {
        const component = CARD_REGISTRY[rule.id as keyof typeof CARD_REGISTRY];
        
        if (!component) {
          console.warn(`Component not found in registry for card: ${rule.id}`);
          return null;
        }

        return {
          id: rule.id,
          component,
          props: rule.getProps?.(state) || {},
          order: rule.priority,
          visible: true,
          slot: rule.slot,
        };
      })
      .filter((card) => card !== null) as CardConfig[];

    return visibleCards;
  }, [state]);

  return {
    cards,
    isLoading: state.isLoading,
    version: 'v2-config-driven',
  };
};

/**
 * Hook to get dashboard configuration for a specific slot
 */
export const useDashboardSlot = (slot: string) => {
  const { cards, isLoading } = useDashboardConfig();
  
  const slotCards = useMemo(() => {
    return cards.filter(card => card.slot === slot);
  }, [cards, slot]);

  return { cards: slotCards, isLoading };
};